import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Col,
  Collapse,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faPlus,
  faChevronDown,
  faChevronUp,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { actions, services } from '../../../context';
import { ConfigurationReadResponseItem, ConfigurationUniversalSource } from '../../types';

const SENSITIVE_KEYS = new Set(['pms_password', 'authorization']);

const NULL_KEY_PLACEHOLDER = '*NULL KEY*';
const keyOf = (item: ConfigurationReadResponseItem): string => item.key ?? NULL_KEY_PLACEHOLDER;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  instanceId: string;
}

interface ScopeBadge {
  label: string;
  color: string;
  name?: string;
  id?: string;
}

// Lookup maps resolving the GUID stored on a source to its human-readable name.
interface NameMaps {
  pims: Map<string, string>;
  bitwerx: Map<string, string>;
  partnerOffering: Map<string, string>;
}

// The instance-anchored scope dimensions an operator can retarget an edit to (instance is always
// anchored, so it is not part of this shape). Empty string means "not scoped at this level".
interface RowScope {
  pimsId: string;
  bitwerxOfferingId: string;
  partnerOfferingId: string;
}

// The scope levels a single source targets (a source can hit several at once, e.g. instance + pims).
const sourceLevels = (s: ConfigurationUniversalSource): string[] => {
  const levels: string[] = [];
  if (s.instanceScheduleId) levels.push('Schedule');
  if (s.instanceId) levels.push('Instance');
  if (s.partnerOfferingId) levels.push('PartnerOffering');
  if (s.bitwerxOfferingId) levels.push('BitwerxOffering');
  if (s.pimsVersionId) levels.push('PimsVersion');
  if (s.pimsId) levels.push('Pims');
  if (levels.length === 0) levels.push('Global');
  return levels;
};

const LEVEL_OPTIONS = [
  'Instance',
  'Schedule',
  'PartnerOffering',
  'BitwerxOffering',
  'PimsVersion',
  'Pims',
  'Global',
  'Unused',
] as const;

// A single source can target multiple scope dimensions at once (e.g. instance + pims),
// so return every applicable badge rather than just the highest one. Name-bearing levels
// (Pims, BitwerxOffering, PartnerOffering) carry the resolved name plus the raw id.
const scopeBadges = (s: ConfigurationUniversalSource, maps: NameMaps): ScopeBadge[] => {
  const badges: ScopeBadge[] = [];
  if (s.instanceScheduleId) badges.push({ label: 'Schedule', color: 'success' });
  if (s.instanceId) badges.push({ label: 'Instance', color: 'success' });
  if (s.partnerOfferingId)
    badges.push({
      label: 'PartnerOffering',
      color: 'warning',
      name: maps.partnerOffering.get(s.partnerOfferingId),
      id: s.partnerOfferingId,
    });
  if (s.bitwerxOfferingId)
    badges.push({
      label: 'BitwerxOffering',
      color: 'warning',
      name: maps.bitwerx.get(s.bitwerxOfferingId),
      id: s.bitwerxOfferingId,
    });
  if (s.pimsVersionId) badges.push({ label: 'PimsVersion', color: 'info' });
  if (s.pimsId)
    badges.push({ label: 'Pims', color: 'info', name: maps.pims.get(s.pimsId), id: s.pimsId });
  if (badges.length === 0) badges.push({ label: 'Global', color: 'secondary' });
  return badges;
};

// Renders the scope badges for a source: name prominent inside the badge, raw id de-emphasized beside it.
const ScopeBadges: React.FC<{ source: ConfigurationUniversalSource; maps: NameMaps }> = ({
  source,
  maps,
}) => (
  <>
    {scopeBadges(source, maps).map(b => (
      <Badge
        key={b.label}
        color={b.color}
        className="mr-1"
        title={b.id}
        style={b.id ? { cursor: 'help' } : undefined}>
        {b.name ? `${b.label}: ${b.name}` : b.label}
      </Badge>
    ))}
  </>
);

type Option = { value: string; label: string };
type BitwerxStatus = 'idle' | 'loading' | 'loaded' | 'error';

// A bare <select> sizes itself to whichever option is currently selected, so picking a long
// offering name would widen the control and reflow the whole row (and shrink it again on reset).
// Pin a fixed width so the pickers hold their place; long labels truncate instead. maxWidth keeps
// them inside the column on narrow viewports, where the flex container wraps them anyway.
const SCOPE_SELECT_STYLE: React.CSSProperties = { width: '14rem', maxWidth: '100%' };

// The instance-anchored scope pickers, shared by the add form and the per-row edit flow. Instance
// is always implied (shown as a fixed badge); the three selects add optional PIMS / Offering /
// Partner-Offering scoping. The Offering select is gated on the catalog load state so a failed
// catalog surfaces an error rather than an empty-but-enabled control.
const ScopeSelectors: React.FC<{
  scope: RowScope;
  pimsOptions: Option[];
  bitwerxOptions: Option[];
  partnerOfferingOptions: Option[];
  bitwerxStatus: BitwerxStatus;
  disabled?: boolean;
  onChange: (patch: Partial<RowScope>) => void;
}> = ({
  scope,
  pimsOptions,
  bitwerxOptions,
  partnerOfferingOptions,
  bitwerxStatus,
  disabled,
  onChange,
}) => (
  <div className="d-flex flex-wrap align-items-center py-2">
    <Badge color="success" className="mr-3 mb-2">
      Instance
    </Badge>
    <select
      className="p-2 rounded border mr-3 mb-2"
      style={SCOPE_SELECT_STYLE}
      value={scope.pimsId}
      onChange={e => onChange({ pimsId: e.target.value })}
      disabled={disabled}>
      <option value="">+ PIMS…</option>
      {pimsOptions.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <select
      className="p-2 rounded border mr-3 mb-2"
      style={SCOPE_SELECT_STYLE}
      value={scope.bitwerxOfferingId}
      onChange={e => onChange({ bitwerxOfferingId: e.target.value })}
      disabled={disabled || bitwerxStatus !== 'loaded'}>
      <option value="">{bitwerxStatus === 'loading' ? 'Loading offerings…' : '+ Offering…'}</option>
      {bitwerxOptions.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <select
      className="p-2 rounded border mb-2"
      style={SCOPE_SELECT_STYLE}
      value={scope.partnerOfferingId}
      onChange={e => onChange({ partnerOfferingId: e.target.value })}
      disabled={disabled}>
      <option value="">+ Partner Offering…</option>
      {partnerOfferingOptions.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {bitwerxStatus === 'error' && (
      <span className="text-warning ml-1 mb-2" title="The offering catalog could not be loaded.">
        Offerings unavailable
      </span>
    )}
  </div>
);

export const PracticeUniversalConfigModal: React.FC<Props> = ({ isOpen, onClose, instanceId }) => {
  const dispatch = useDispatch();
  const { configs, loading } = useSelector(
    (s: {
      configurationUniversal: { configs: ConfigurationReadResponseItem[]; loading: boolean };
    }) => s.configurationUniversal,
  );

  // The practice being viewed — its own PIMS and connected offerings define which scope
  // combinations the backend will accept for an instance-anchored config.
  const practice = useSelector(
    (s: {
      practice: {
        practice?: {
          pimsId?: string;
          pims?: string;
          practiceOfferings?: {
            id: string;
            name: string;
            partner?: string;
            bitwerxOfferingIds?: string[];
          }[];
        };
      };
    }) => s.practice.practice,
  );

  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Optional additional scope levels for a new config (instance is always anchored).
  const [newPimsId, setNewPimsId] = useState('');
  const [newBitwerxOfferingId, setNewBitwerxOfferingId] = useState('');
  const [newPartnerOfferingId, setNewPartnerOfferingId] = useState('');

  // Per-row scope overrides for the edit flow, keyed by config key. An entry only exists once the
  // operator has touched a scope selector for that row; while absent, Save replays the winning
  // source's own scope — in place for an instance-anchored winner, or a bare instance-only write
  // for an inherited one. When present, Save retargets the write to the chosen instance-anchored
  // combination. See handleSave.
  const [scopeDrafts, setScopeDrafts] = useState<Record<string, RowScope>>({});

  // Collapsible search / level filter (collapsed by default).
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [levelFilter, setLevelFilter] = useState(''); // '' = all levels

  // PIMS names come free from the global store (loaded app-wide on auth).
  const allPims = useSelector(
    (s: { pims: { allPims: { id: string; name: string }[] } }) => s.pims.allPims,
  );
  const pimsNames = useMemo(
    () => new Map((allPims ?? []).map(p => [p.id, p.name] as const)),
    [allPims],
  );

  // Bitwerx offering names (global id→name) aren't in any store reachable from this legacy tree,
  // so fetch the catalog once when the modal opens. The catalog is also the authority on which
  // offerings are selectable: only ids it resolves are offered as options, so soft-deleted
  // offerings and (when the endpoint is unavailable) raw GUIDs are never presented as valid.
  const [bitwerxNames, setBitwerxNames] = useState<Map<string, string>>(new Map());
  const [bitwerxStatus, setBitwerxStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle',
  );

  // Partner offering names come from the practice's own connected offerings (correct
  // PartnerOffering.Id → name). The global offerings/partners endpoint is grouped by Partner and
  // exposes Partner.Id, which is the wrong key for a config's partnerOfferingId.
  const partnerOfferingNames = useMemo(
    () => new Map((practice?.practiceOfferings ?? []).map(o => [o.id, o.name] as const)),
    [practice],
  );

  const nameMaps = useMemo<NameMaps>(
    () => ({ pims: pimsNames, bitwerx: bitwerxNames, partnerOffering: partnerOfferingNames }),
    [pimsNames, bitwerxNames, partnerOfferingNames],
  );

  // Dropdown options for the new-config scope pickers — limited to what this practice can
  // legitimately combine with (matches the backend's candidate scoping for an instance-anchored write).
  // PIMS: only the practice's own PIMS is a valid combination.
  const pimsOptions = useMemo(
    () =>
      practice?.pimsId ? [{ value: practice.pimsId, label: practice.pims ?? practice.pimsId }] : [],
    [practice],
  );
  // Partner Offerings: the practice's connected partner offerings (correct PartnerOffering.Id).
  // When a Bitwerx Offering is already chosen, restrict to the POs that actually link it —
  // any other combination would be rejected by the backend. Parameterized on the chosen Bitwerx
  // id so both the add form and each edited row can build their own cross-filtered list.
  const buildPartnerOfferingOptions = useCallback(
    (selectedBitwerxOfferingId: string) => {
      const all = practice?.practiceOfferings ?? [];
      const filtered = selectedBitwerxOfferingId
        ? all.filter(o => (o.bitwerxOfferingIds ?? []).includes(selectedBitwerxOfferingId))
        : all;
      return filtered
        .map(o => ({ value: o.id, label: o.partner ? `${o.partner} – ${o.name}` : o.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [practice],
  );
  // Bitwerx Offerings: distinct ids linked to the practice's offerings, but only those the loaded
  // catalog can resolve to a name. Unresolved ids — soft-deleted offerings, or every id when the
  // catalog endpoint is unavailable to this operator — are dropped rather than offered as raw
  // GUIDs, so the picker never presents an option the config provider would reject. When a Partner
  // Offering is already chosen, restrict to that PO's bitwerxOfferingIds so the pairing stays valid.
  const buildBitwerxOptions = useCallback(
    (selectedPartnerOfferingId: string) => {
      const offerings = practice?.practiceOfferings ?? [];
      const source = selectedPartnerOfferingId
        ? offerings.filter(o => o.id === selectedPartnerOfferingId)
        : offerings;
      const ids = new Set(source.flatMap(o => o.bitwerxOfferingIds ?? []));
      return Array.from(ids)
        .filter(id => bitwerxNames.has(id))
        .map(id => ({ value: id, label: bitwerxNames.get(id) as string }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [practice, bitwerxNames],
  );
  const partnerOfferingOptions = useMemo(
    () => buildPartnerOfferingOptions(newBitwerxOfferingId),
    [buildPartnerOfferingOptions, newBitwerxOfferingId],
  );
  const bitwerxOptions = useMemo(
    () => buildBitwerxOptions(newPartnerOfferingId),
    [buildBitwerxOptions, newPartnerOfferingId],
  );

  // Clear the counterpart selection when it becomes invalid under the current pairing (e.g. the
  // user switches to a PO that doesn't link the previously-chosen BO, or vice versa).
  useEffect(() => {
    if (newBitwerxOfferingId && !bitwerxOptions.some(o => o.value === newBitwerxOfferingId)) {
      setNewBitwerxOfferingId('');
    }
  }, [bitwerxOptions, newBitwerxOfferingId]);
  useEffect(() => {
    if (
      newPartnerOfferingId &&
      !partnerOfferingOptions.some(o => o.value === newPartnerOfferingId)
    ) {
      setNewPartnerOfferingId('');
    }
  }, [partnerOfferingOptions, newPartnerOfferingId]);

  // Sort by the winning (selected) source's score descending, then by key A–Z.
  // Unused keys (no selected source) get score 0 and sink to the bottom.
  const sortedConfigs = useMemo(() => {
    return [...configs]
      .map(item => {
        const selected = item.sources.find(s => s.sourceId === item.selectedSourceId);
        return { item, score: selected?.score ?? 0 };
      })
      .sort((a, b) => b.score - a.score || keyOf(a.item).localeCompare(keyOf(b.item)))
      .map(x => x.item);
  }, [configs]);

  // Filter the (already sorted) list by key text and level. Keeps score/name ordering.
  const visibleConfigs = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return sortedConfigs.filter(item => {
      if (q && !keyOf(item).toLowerCase().includes(q)) return false;
      if (levelFilter) {
        const selected = item.sources.find(s => s.sourceId === item.selectedSourceId);
        if (levelFilter === 'Unused') return !selected;
        if (!selected) return false;
        return sourceLevels(selected).includes(levelFilter);
      }
      return true;
    });
  }, [sortedConfigs, searchText, levelFilter]);

  useEffect(() => {
    if (!isOpen) {
      setDraftValues({});
      setNewKey('');
      setNewValue('');
      setExpandedKeys(new Set());
      setSearchOpen(false);
      setSearchText('');
      setLevelFilter('');
      setNewPimsId('');
      setNewBitwerxOfferingId('');
      setNewPartnerOfferingId('');
      setScopeDrafts({});
      setBitwerxNames(new Map());
      setBitwerxStatus('idle');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && instanceId) {
      void dispatch(actions.configurationUniversal.fetchUniversalConfigs(instanceId));
    }
  }, [isOpen, instanceId, dispatch]);

  // Fetch the bitwerx offering catalog on open. This is the source of truth for both the offering
  // names and the set of selectable offerings, so its load state gates the picker: on failure we
  // surface an error and offer nothing rather than falling back to selectable raw GUIDs.
  // The endpoint is paginated (default pageSize 25), so we request a large first page and, if
  // more pages remain, fetch the rest in parallel.
  useEffect(() => {
    if (!isOpen) return;
    const axios = services.axios.Axios();
    let cancelled = false;
    type BitwerxPage = {
      data: { items: { id: string; name: string }[]; totalPages: number };
    };
    const fetchPage = (pageNumber: number) =>
      axios.get('offerings/bitwerx', {
        params: { pageNumber, pageSize: 200 },
      }) as unknown as Promise<BitwerxPage>;
    setBitwerxStatus('loading');
    void (async () => {
      try {
        const first = await fetchPage(1);
        if (cancelled) return;
        const totalPages = first.data.totalPages ?? 1;
        const items = [...(first.data.items ?? [])];
        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2)),
          );
          if (cancelled) return;
          rest.forEach(r => items.push(...(r.data.items ?? [])));
        }
        setBitwerxNames(new Map(items.map(o => [o.id, o.name] as const)));
        setBitwerxStatus('loaded');
      } catch {
        if (cancelled) return;
        // The catalog is unavailable (e.g. the endpoint requires ViewAllPractices, which this
        // advanced-config operator may lack). Surface the failure instead of offering GUIDs.
        setBitwerxNames(new Map());
        setBitwerxStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Takes the item (not just the key) so we can compare against the original value and decide
  // whether to drop a stale scope draft. scopeDrafts is otherwise only cleared on a successful
  // save and on modal close, but the Applies-to row (and the ability to see/undo a retarget) only
  // renders while the row is dirty — so undoing a value edit back to the original hides the draft
  // from view without discarding it, and a later edit would then silently write to a scope the
  // operator never saw as active. Clear it here so an undo really means "abandon the retarget".
  const handleDraftChange = (item: ConfigurationReadResponseItem, value: string) => {
    setDraftValues(prev => ({ ...prev, [item.key]: value }));
    if (value === (item.value ?? '')) {
      setScopeDrafts(prev => {
        if (!(item.key in prev)) return prev;
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    }
  };

  const isDirty = (item: ConfigurationReadResponseItem) =>
    item.key in draftValues && draftValues[item.key] !== (item.value ?? '');

  const handleSave = async (item: ConfigurationReadResponseItem) => {
    const target = item.sources.find(s => s.sourceId === item.selectedSourceId);
    const scopeDraft = scopeDrafts[item.key];
    setSavingKey(item.key);
    try {
      // Three save modes:
      //  - Scope draft present (operator retargeted this edit): write to the chosen instance-
      //    anchored combination. pimsVersionId/instanceScheduleId are carried from the winning
      //    source defensively — the picker itself only exposes the PIMS/Offering/Partner-Offering
      //    dimensions and can't set them, but a pre-existing draft should not silently drop them.
      //    (The canRetarget gate on the row's ScopeSelectors normally prevents a draft from being
      //    created at all when the winner carries either of these — see the row render body.)
      //  - No scope draft, winner is instance-anchored (target.instanceId != null): update the
      //    winning (highest-score) source in place. Sending only { instanceId } would insert a
      //    hidden instance-only ghost row and null the winning source's value, reverting on
      //    refresh, so we replay the winning source's full scope for an exact-score match.
      //  - No scope draft, winner is inherited (target.instanceId == null — Pims/PimsVersion/
      //    Offering-only, no instance in its FK set): write a bare { instanceId }. An instance-only
      //    write (score 32) already outranks any Pims/PimsVersion/Offering combination (max
      //    2+4+8+16=30), so this alone creates a new, winning instance-level override. Replaying
      //    the inherited source's FKs instead — as this branch used to — would pin the override to
      //    the practice's *current* PimsVersion/Offering relation, silently detaching it on the
      //    next version upgrade.
      const scope = scopeDraft
        ? {
            instanceId,
            ...(scopeDraft.pimsId ? { pimsId: scopeDraft.pimsId } : {}),
            ...(scopeDraft.bitwerxOfferingId
              ? { bitwerxOfferingId: scopeDraft.bitwerxOfferingId }
              : {}),
            ...(scopeDraft.partnerOfferingId
              ? { partnerOfferingId: scopeDraft.partnerOfferingId }
              : {}),
            ...(target?.pimsVersionId ? { pimsVersionId: target.pimsVersionId } : {}),
            ...(target?.instanceScheduleId
              ? { instanceScheduleId: target.instanceScheduleId }
              : {}),
          }
        : target?.instanceId != null
          ? {
              instanceId: target.instanceId,
              ...(target.pimsId ? { pimsId: target.pimsId } : {}),
              ...(target.pimsVersionId ? { pimsVersionId: target.pimsVersionId } : {}),
              ...(target.bitwerxOfferingId ? { bitwerxOfferingId: target.bitwerxOfferingId } : {}),
              ...(target.partnerOfferingId ? { partnerOfferingId: target.partnerOfferingId } : {}),
              ...(target.instanceScheduleId
                ? { instanceScheduleId: target.instanceScheduleId }
                : {}),
            }
          : { instanceId };
      await dispatch(
        actions.configurationUniversal.addOrUpdateUniversalConfig(
          { key: item.key, value: draftValues[item.key], ...scope },
          instanceId,
        ),
      );
      setDraftValues(prev => {
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
      setScopeDrafts(prev => {
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    } catch {
      // The thunk already toasted the error; this only prevents an unhandled promise rejection at
      // the `void handleSave(item)` call site. draftValues/scopeDrafts are intentionally left
      // untouched (the clears above only run on the success path) so the operator's edit and any
      // retarget survive for a retry.
    } finally {
      setSavingKey(null);
    }
  };

  const handleRemove = async (item: ConfigurationReadResponseItem) => {
    // Target the winning (highest-score) source so the backend deletes the exact row the trash
    // represents. Passing its full FK set makes request.Score match the row's score.
    const target = item.sources.find(s => s.sourceId === item.selectedSourceId);
    setRemovingKey(item.key);
    try {
      await dispatch(
        actions.configurationUniversal.removeUniversalConfig(
          {
            key: item.key,
            instanceId: target?.instanceId ?? instanceId,
            ...(target?.pimsId ? { pimsId: target.pimsId } : {}),
            ...(target?.pimsVersionId ? { pimsVersionId: target.pimsVersionId } : {}),
            ...(target?.bitwerxOfferingId ? { bitwerxOfferingId: target.bitwerxOfferingId } : {}),
            ...(target?.partnerOfferingId ? { partnerOfferingId: target.partnerOfferingId } : {}),
            ...(target?.instanceScheduleId
              ? { instanceScheduleId: target.instanceScheduleId }
              : {}),
          },
          instanceId,
        ),
      );
    } catch {
      // The thunk already toasted the error; swallow here only to avoid an unhandled promise
      // rejection at the `void handleRemove(item)` call site.
    } finally {
      setRemovingKey(null);
    }
  };

  const handleAdd = async () => {
    if (!newKey.trim() || !newValue.trim()) return;
    setSaving(true);
    try {
      await dispatch(
        actions.configurationUniversal.addOrUpdateUniversalConfig(
          {
            key: newKey.trim(),
            value: newValue.trim(),
            instanceId,
            ...(newPimsId ? { pimsId: newPimsId } : {}),
            ...(newBitwerxOfferingId ? { bitwerxOfferingId: newBitwerxOfferingId } : {}),
            ...(newPartnerOfferingId ? { partnerOfferingId: newPartnerOfferingId } : {}),
          },
          instanceId,
        ),
      );
      setNewKey('');
      setNewValue('');
      setNewPimsId('');
      setNewBitwerxOfferingId('');
      setNewPartnerOfferingId('');
    } catch {
      // The thunk already toasted the error; swallow here only to avoid an unhandled promise
      // rejection at the `void handleAdd()` call site. The new-config fields above are cleared
      // only on the success path, so a failed add preserves what the operator typed for a retry.
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // The scope the row's selectors should display: the operator's draft once they've touched it,
  // otherwise seeded from the winning source so the controls open pre-filled with the current
  // scope. Values that the (catalog-filtered / practice-scoped) option lists can't resolve simply
  // render as the placeholder — they are never presented as selectable.
  const rowScopeFor = (item: ConfigurationReadResponseItem): RowScope => {
    const draft = scopeDrafts[item.key];
    if (draft) return draft;
    const src = item.sources.find(s => s.sourceId === item.selectedSourceId);
    return {
      pimsId: src?.pimsId ?? '',
      bitwerxOfferingId: src?.bitwerxOfferingId ?? '',
      partnerOfferingId: src?.partnerOfferingId ?? '',
    };
  };

  // Update one scope dimension for a row, materializing the draft (seeded from the current scope)
  // on first touch. Only the dimension actually present in `patch` triggers its counterpart's
  // clearing check — validating both checks against the merged `next` regardless of which
  // dimension changed (the previous behavior) was wrong on two counts: (1) bitwerxNames is empty
  // whenever the catalog hasn't loaded (or failed to), so buildBitwerxOptions() always returns
  // nothing and a seeded bitwerxOfferingId would get silently cleared the moment the operator
  // touched the still-enabled PIMS select, even though they never touched the offering pairing;
  // and (2) the checks were reading the field just patched instead of its counterpart, clearing
  // e.g. partnerOfferingId right after the operator picked it. Each check below now fires only for
  // the patched dimension and clears only its counterpart.
  const changeRowScope = (item: ConfigurationReadResponseItem, patch: Partial<RowScope>) => {
    const next: RowScope = { ...rowScopeFor(item), ...patch };
    if (
      patch.bitwerxOfferingId !== undefined &&
      next.partnerOfferingId &&
      !buildPartnerOfferingOptions(next.bitwerxOfferingId).some(
        o => o.value === next.partnerOfferingId,
      )
    ) {
      next.partnerOfferingId = '';
    }
    if (
      patch.partnerOfferingId !== undefined &&
      next.bitwerxOfferingId &&
      !buildBitwerxOptions(next.partnerOfferingId).some(o => o.value === next.bitwerxOfferingId)
    ) {
      next.bitwerxOfferingId = '';
    }
    setScopeDrafts(prev => ({ ...prev, [item.key]: next }));
  };

  return (
    <Modal style={{ minWidth: '75vw' }} isOpen={isOpen} toggle={onClose} centered={true}>
      <ModalHeader className="justify-content-between text-light">
        Advanced Practice Configurations
      </ModalHeader>
      <div className="text-light">
        New configurations are anchored to this instance. Use the “Applies to” selectors to
        additionally scope a value to the practice’s PIMS or a connected offering. Editing an
        inherited value creates a new override anchored to this instance; editing a value already
        scoped to this instance updates it in place.
      </div>
      <ModalBody className="justify-content-center">
        <div className="px-3 pt-2">
          <Button color="secondary" size="sm" onClick={() => setSearchOpen(o => !o)}>
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            Search &amp; filter
            <FontAwesomeIcon icon={searchOpen ? faChevronUp : faChevronDown} className="ml-2" />
          </Button>

          <Collapse isOpen={searchOpen}>
            <Row className="pt-2 align-items-center">
              <Col md="7" xs="12" className="mb-2 mb-md-0">
                <input
                  className="w-100 p-2 rounded border"
                  placeholder="Search by key name…"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </Col>
              <Col md="4" xs="9">
                <select
                  className="w-100 p-2 rounded border"
                  value={levelFilter}
                  onChange={e => setLevelFilter(e.target.value)}>
                  <option value="">All levels</option>
                  {LEVEL_OPTIONS.map(l => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </Col>
              <Col md="1" xs="3" className="text-right">
                {(searchText || levelFilter) && (
                  <Button
                    color="link"
                    size="sm"
                    className="text-light p-0"
                    onClick={() => {
                      setSearchText('');
                      setLevelFilter('');
                    }}>
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </Collapse>
        </div>

        {loading && <h3 className="text-center text-light">Loading...</h3>}

        {!loading && configs.length === 0 && (
          <p className="text-center text-light">No configurations found for this instance.</p>
        )}

        {!loading && configs.length > 0 && visibleConfigs.length === 0 && (
          <p className="text-center text-light">No configurations match your search.</p>
        )}

        {!loading &&
          visibleConfigs.map(item => {
            const selectedSource = item.sources.find(s => s.sourceId === item.selectedSourceId);
            const canDelete =
              selectedSource?.sourceEntity === 'ConfigurationUniversal' &&
              selectedSource?.instanceId != null;
            const isSensitive = SENSITIVE_KEYS.has(item.key);
            const isExpanded = expandedKeys.has(item.key);
            const dirty = isDirty(item);
            const rowScope = rowScopeFor(item);

            // Fix B: a winner that is schedule-scoped but not instance-anchored (instanceId ==
            // null, instanceScheduleId != null — production creates these) can't be edited from
            // this modal. An instance-only write (score 32) always loses to a source carrying
            // Schedule (score 64+), so an edit would post successfully, get out-scored on refetch,
            // and silently do nothing. Refuse to edit rather than let that look like it worked.
            const editable = !(
              selectedSource &&
              selectedSource.instanceId == null &&
              selectedSource.instanceScheduleId != null
            );

            // The "Applies to" selectors can only express Pims / BitwerxOffering /
            // PartnerOffering scoping — never PimsVersion or Schedule. If the winning source
            // carries either of those, retargeting from this picker would build a draft that can't
            // represent the row's real scope (and handleSave would have to carry them through
            // defensively — see there), so refuse to retarget instead.
            const scopeRepresentable =
              !selectedSource?.pimsVersionId && !selectedSource?.instanceScheduleId;
            const canRetarget = scopeRepresentable && bitwerxStatus === 'loaded';

            return (
              <React.Fragment key={item.key ?? item.sources[0]?.sourceId ?? NULL_KEY_PLACEHOLDER}>
                <Row className="border-bottom text-light text-16 p-3 align-items-center">
                  <Col lg="4" className="text-right d-none d-lg-block font-weight-bold">
                    {keyOf(item)}{' '}
                    {selectedSource ? (
                      <ScopeBadges source={selectedSource} maps={nameMaps} />
                    ) : (
                      <Badge color="danger">unused</Badge>
                    )}
                    {!editable && (
                      <Badge color="secondary" className="ml-1">
                        Schedule-scoped — not editable here
                      </Badge>
                    )}
                  </Col>
                  <Col xs="12" className="d-lg-none font-weight-bold">
                    {keyOf(item)}{' '}
                    {selectedSource ? (
                      <ScopeBadges source={selectedSource} maps={nameMaps} />
                    ) : (
                      <Badge color="danger">unused</Badge>
                    )}
                    {!editable && (
                      <Badge color="secondary" className="ml-1">
                        Schedule-scoped — not editable here
                      </Badge>
                    )}
                  </Col>
                  <Col lg="7" md="10" xs="10">
                    <input
                      type={isSensitive ? 'password' : 'text'}
                      className="w-100 p-2 rounded border"
                      value={draftValues[item.key] ?? item.value ?? ''}
                      onChange={e => handleDraftChange(item, e.target.value)}
                      readOnly={!editable}
                      title={
                        editable
                          ? undefined
                          : 'This value is schedule-scoped and cannot be edited from this modal.'
                      }
                    />
                  </Col>
                  <Col lg="1" md="2" xs="2" className="d-flex gap-1 justify-content-end">
                    {dirty && editable && (
                      <Button
                        color="primary"
                        size="sm"
                        disabled={savingKey === item.key}
                        onClick={() => void handleSave(item)}>
                        {savingKey === item.key ? '…' : 'Save'}
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        color="danger"
                        size="sm"
                        disabled={removingKey === item.key}
                        onClick={() => void handleRemove(item)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    )}
                    <Button color="secondary" size="sm" onClick={() => toggleExpanded(item.key)}>
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </Button>
                  </Col>
                </Row>

                {/* Scope selectors for the edit flow — revealed once the value is edited, seeded
                    from the current scope so an unchanged scope saves in place (see handleSave). */}
                {dirty && (
                  <Row className="p-3 text-light align-items-center">
                    <Col lg="4" className="d-none d-lg-block text-right font-weight-bold">
                      Applies to
                    </Col>
                    <Col xs="12" className="d-lg-none font-weight-bold mb-1">
                      Applies to
                    </Col>
                    <Col xs="12" lg="8">
                      <ScopeSelectors
                        scope={rowScope}
                        pimsOptions={pimsOptions}
                        bitwerxOptions={buildBitwerxOptions(rowScope.partnerOfferingId)}
                        partnerOfferingOptions={buildPartnerOfferingOptions(
                          rowScope.bitwerxOfferingId,
                        )}
                        bitwerxStatus={bitwerxStatus}
                        disabled={savingKey === item.key || !canRetarget}
                        onChange={patch => changeRowScope(item, patch)}
                      />
                      {/* Disable the whole row (Pims + Partner-Offering + Offering selects, via
                          ScopeSelectors' own `disabled`) rather than just the Offering select —
                          the reasons below are mutually exclusive, checked in priority order. */}
                      {!canRetarget && (
                        <div className="text-warning mt-1">
                          {!scopeRepresentable
                            ? 'This row’s current scope uses PimsVersion or Schedule, which these selectors can’t represent — retargeting is unavailable.'
                            : bitwerxStatus === 'loading'
                              ? 'Loading the offering catalog…'
                              : 'The offering catalog is unavailable, so retargeting is disabled.'}
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                {isExpanded && item.sources && item.sources.length > 0 && (
                  <Row className="px-3 pb-2">
                    <Col xs="12">
                      <table className="table table-sm table-dark text-light mb-0 mt-1">
                        <thead>
                          <tr>
                            <th>Entity</th>
                            <th>Scope</th>
                            {/* <th>Score</th> */}
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.sources.map((src, idx) => {
                            const isSelected = src.sourceId === item.selectedSourceId;
                            return (
                              <tr key={idx} className={isSelected ? 'table-primary text-dark' : ''}>
                                <td>{src.sourceEntity ?? '—'}</td>
                                <td>
                                  <ScopeBadges source={src} maps={nameMaps} />
                                  <span className="text-muted">({src.score ?? ''})</span>
                                </td>
                                <td>
                                  {isSensitive
                                    ? '••••••'
                                    : (src.value ?? <span className="text-muted">null</span>)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Col>
                  </Row>
                )}
              </React.Fragment>
            );
          })}

        {/* Add new config row */}
        <Row className="pt-3 px-3 align-items-center">
          <Col lg="4" className="d-none d-lg-block text-right font-weight-bold text-light">
            New configuration
          </Col>
          <Col xs="12" className="d-lg-none font-weight-bold text-light mb-1">
            New configuration
          </Col>
          <Col lg="4" md="6" xs="6">
            <input
              className="w-100 p-2 rounded border"
              placeholder="Key"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              disabled={loading || saving}
            />
          </Col>
          <Col lg="3" md="4" xs="4">
            <input
              className="w-100 p-2 rounded border"
              placeholder="Value"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              disabled={loading || saving}
            />
          </Col>
          <Col lg="1" md="2" xs="2">
            <Button
              color="primary"
              size="sm"
              disabled={!newKey.trim() || !newValue.trim() || saving || loading}
              onClick={() => void handleAdd()}>
              {saving ? '…' : <FontAwesomeIcon icon={faPlus} />}
            </Button>
          </Col>
        </Row>

        {/* Optional additional scope levels for the new config (instance is always anchored) */}
        <Row className="pb-3 px-3 align-items-center">
          <Col lg="4" className="d-none d-lg-block text-right font-weight-bold text-light">
            Applies to
          </Col>
          <Col xs="12" lg="8">
            <ScopeSelectors
              scope={{
                pimsId: newPimsId,
                bitwerxOfferingId: newBitwerxOfferingId,
                partnerOfferingId: newPartnerOfferingId,
              }}
              pimsOptions={pimsOptions}
              bitwerxOptions={bitwerxOptions}
              partnerOfferingOptions={partnerOfferingOptions}
              bitwerxStatus={bitwerxStatus}
              disabled={loading || saving}
              onChange={patch => {
                if (patch.pimsId !== undefined) setNewPimsId(patch.pimsId);
                if (patch.bitwerxOfferingId !== undefined) {
                  setNewBitwerxOfferingId(patch.bitwerxOfferingId);
                }
                if (patch.partnerOfferingId !== undefined) {
                  setNewPartnerOfferingId(patch.partnerOfferingId);
                }
              }}
            />
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="justify-content-end">
        <Button color="info" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
